import { useEffect, useRef } from 'react';
import { avatarStore } from '@/stores/avatarStore';
import { apiSwaggerClientSide } from '@/shared/api';
import { useIsUserAuthorized } from '@/utils/authHelpers';

export const useGlobalAvatar = () => {
  const isUserAuthorized = useIsUserAuthorized();
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Only make API call if user is authorized and we haven't loaded yet
    if (!isUserAuthorized) {
      hasLoadedRef.current = false; // Reset when user becomes unauthorized
      return;
    }

    // Prevent multiple calls if already loaded
    if (hasLoadedRef.current) {
      return;
    }

    hasLoadedRef.current = true;

    apiSwaggerClientSide.user.detailsList()
      .then((res) => {
        if (res.data?.displayName) {
          avatarStore.setDisplayName(res.data.displayName);
        }
        if (res.data?.avatarURL) {
          avatarStore.updateAvatarPath(res.data.avatarURL);
        }
      })
      .catch(() => {
        hasLoadedRef.current = false; // Reset on error to allow retry
      });
  }, [isUserAuthorized]);

  return {
    avatarUrl: avatarStore.finalAvatarUrl,
    displayName: avatarStore.displayName,
    loading: avatarStore.loading,
    error: avatarStore.error,
    setOptimisticAvatar: (url: string | null) =>
      avatarStore.setOptimisticUrl(url),
    clearOptimistic: () => avatarStore.clearOptimistic(),
    forceReload: (path?: string | null) => {
      hasLoadedRef.current = false; // Reset flag to allow reload
      avatarStore.forceReload(path ?? avatarStore.currentAvatarPath ?? '');
    },
    store: avatarStore,
  };
};
